import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, MapPin, CheckCircle2, XCircle, AlertTriangle, Clock, Download, Video, Bug } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import api from "lib/api";
import { livenessImageUrl, livenessReportUrl, livenessVideoUrl } from "lib/media";
import { useI18n } from "i18n";
import { useAuth } from "contexts/AuthContext";

const STATUS_CLS = {
  passed:  "bg-emerald-400/10 text-emerald-500 border-emerald-400/20",
  failed:  "bg-red-400/10 text-red-500 border-red-400/20",
  pending: "bg-amber-400/10 text-amber-500 border-amber-400/20",
  expired: "bg-muted text-muted-foreground border-border",
};

const pct = (v) => (v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`);
const fmtUtc = (iso) => (iso ? `${iso.slice(0, 10)} ${iso.slice(11, 19)} UTC` : "—");

function Row({ label, children, icon: Icon }) {
  return (
    <div className="flex items-start gap-4 py-1.5">
      <div className="w-44 shrink-0 text-sm text-muted-foreground inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-4 w-4" />} {label}
      </div>
      <div className="text-sm min-w-0">{children}</div>
    </div>
  );
}

function EvidenceChips({ items }) {
  if (!items?.length) return null;
  // De-dup by text (re-runs of the deep PAD can accumulate repeated findings).
  const seen = new Set();
  const unique = [];
  for (const ev of items) {
    const text = typeof ev === "string" ? ev : ev.text;
    const key = (text || "").replace(/\s+/g, " ").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(ev);
  }
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {unique.map((ev, i) => {
        const text = typeof ev === "string" ? ev : ev.text;
        const cat = typeof ev === "string" ? "neutral" : ev.category;
        const cls = cat === "issue"
          ? "border-red-500/50 text-red-400"
          : cat === "positive"
          ? "border-emerald-500/50 text-emerald-400"
          : "border-border text-muted-foreground";
        return (
          <span key={i} className={`px-2.5 py-1 rounded-lg border text-xs ${cls}`}>{text}</span>
        );
      })}
    </div>
  );
}

function Photo({ url, label, sub }) {
  return (
    <div className="text-center">
      <img src={url} alt={label}
        className="h-40 w-auto rounded-lg border border-border object-cover mx-auto bg-black" />
      <p className="text-xs text-muted-foreground mt-1.5">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}

// Review detail of one liveness verification (opened in a new tab from the list).
export default function SessionDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = !!user?.capabilities?.manage_system;
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    api.get(`/api/sessions/${id}/`)
      .then((r) => setD(r.data))
      .catch((e) => setError(e?.response?.status === 404
        ? t("sessionDetail.notFound") : t("sessionDetail.loadFailed")));
  }, [id, t]);

  if (error) {
    return <div className="text-center py-16 text-sm text-muted-foreground">
      <XCircle className="h-10 w-10 mx-auto mb-3 text-red-500" />{error}</div>;
  }
  if (!d) {
    return <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;
  }

  const stKey = d.effective_status || d.status;
  const stCls = STATUS_CLS[stKey] || STATUS_CLS.pending;
  const stLabel = t(`status.${stKey}`, t("status.pending"));
  const deep = d.deep || {};
  const report = deep.report || {};
  const geo = d.geo || {};
  const liv = d.liveness || {};
  const ges = d.gesture || {};
  const padOk = deep.status === "done" && !deep.flagged;
  const motion = deep.motion || null;
  const flash = deep.flash || null;
  const location = [geo.city, geo.state, geo.country].filter(Boolean).join(", ");

  return (
    <div className="animate-fade-in w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">{t("sessionDetail.title")}</h1>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${stCls}`}>{stLabel}</span>
        {deep.status === "done" && (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
            padOk ? "bg-emerald-400/10 text-emerald-500 border-emerald-400/20"
                  : "bg-red-400/10 text-red-500 border-red-400/20"}`}>
            {padOk ? <ShieldCheck className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {t("sessionDetail.padLabel")}: {padOk ? t("sessionDetail.padOk") : t("sessionDetail.padFlagged")}
          </span>
        )}
        {deep.status !== "done" && (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-muted text-muted-foreground border-border">
            <Clock className="h-3.5 w-3.5" /> {t("sessionDetail.padLabel")}: {deep.status === "error" ? t("sessionDetail.padError") : t("sessionDetail.padAnalyzing")}
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {d.subject_name || d.subject?.name} · {d.subject?.email || ""}
        </span>
        <button
          type="button"
          onClick={() => setShowVideo((v) => !v)}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm transition-colors ${
            showVideo ? "border-primary/40 text-primary bg-primary/10"
                      : "border-border text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/10"}`}>
          <Video className="h-4 w-4" /> {showVideo ? t("video.hide") : t("video.watch")}
        </button>
        <button
          type="button"
          title={t("report.download")}
          aria-label={t("report.download")}
          onClick={() => window.open(livenessReportUrl(d.id), "_blank", "noopener")}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-colors">
          <Download className="h-4 w-4" /> PDF
        </button>
        <button
          type="button"
          title={t("video.download", "Baixar vídeo")}
          aria-label={t("video.download", "Baixar vídeo")}
          onClick={() => window.open(livenessVideoUrl(d.id) + "&download=1", "_blank", "noopener")}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-colors">
          <Download className="h-4 w-4" /> MP4
        </button>
        {isSuperAdmin && (
          <button
            type="button"
            title={t("debug.title", "Debug da IA")}
            aria-label={t("debug.title", "Debug da IA")}
            onClick={() => navigate(`/liveness/sessions/${d.id}/debug`)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-colors">
            <Bug className="h-4 w-4" /> Debug
          </button>
        )}
      </div>

      <Card><CardContent className="p-6">
        {/* Recorded liveness video (remuxed on demand from the server recording) */}
        {showVideo && (
          <div className="mb-6 w-full">
            <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
              <Video className="h-4 w-4" /> {t("video.title")}
            </div>
            <video
              key={d.id}
              src={livenessVideoUrl(d.id)}
              controls
              autoPlay
              className="w-full max-h-[70vh] rounded-lg border border-border bg-black"
              onError={(e) => { e.currentTarget.insertAdjacentHTML("afterend",
                `<p class="text-sm text-muted-foreground mt-2">${t("video.unavailable")}</p>`); }}
            />
          </div>
        )}
        {/* Photos */}
        <div className="flex flex-wrap gap-6 mb-6">
          {d.has_images?.face && <Photo url={livenessImageUrl(d.id)} label={t("sessionDetail.photoFace")} />}
          {d.has_images?.full && <Photo url={livenessImageUrl(d.id, "full")} label={t("sessionDetail.photoFull")} />}
          {d.has_images?.gesture && (
            <Photo url={livenessImageUrl(d.id, "gesture")} label={t("sessionDetail.photoGesture")}
              sub={ges.probability != null ? pct(ges.probability) : ""} />
          )}
        </div>

        {/* Facts */}
        <div className="divide-y divide-border/60">
          <Row label={t("sessionDetail.completedAt")}>{fmtUtc(liv.completed_at)}</Row>
          <Row label={t("sessionDetail.geolocation")} icon={MapPin}>
            {geo.latitude != null ? (
              <>
                <a className="text-primary hover:underline" target="_blank" rel="noreferrer"
                  href={`https://www.google.com/maps?q=${geo.latitude},${geo.longitude}`}>
                  {geo.latitude?.toFixed(6)}, {geo.longitude?.toFixed(6)}
                </a>
                {geo.accuracy_m != null && <span className="text-muted-foreground"> · ±{Math.round(geo.accuracy_m)} m</span>}
              </>
            ) : <span className="text-muted-foreground">{geo.error || t("sessionDetail.geoNotProvided")}</span>}
          </Row>
          <Row label={t("sessionDetail.location")}>
            {location ? (
              <>
                {location}
                <span className="text-muted-foreground text-xs ml-2">
                  ({geo.source === "geo" ? t("sessionDetail.viaGeo") : geo.source === "ip" ? t("sessionDetail.viaIp") : ""})
                </span>
              </>
            ) : <span className="text-muted-foreground">—</span>}
            {geo.client_ip && <span className="block text-xs text-muted-foreground font-mono mt-0.5">IP {geo.client_ip}</span>}
          </Row>
          <Row label={t("sessionDetail.localDetection")}>
            {t("sessionDetail.detShort")} {liv.det_score?.toFixed(2)} · {t("sessionDetail.frontality")} {liv.frontality?.toFixed(2)}
          </Row>
          {motion && (
            <Row label={t("sessionDetail.depth")}>
              <span className={motion.verdict === "REAL_3D" ? "text-emerald-500"
                : motion.verdict === "FLAT_SPOOF" ? "text-red-500" : "text-muted-foreground"}>
                {t(`reason.${motion.verdict}`, t("reason.inconclusive"))}
                {motion.score != null && <> · {pct(motion.score)}</>}
              </span>
            </Row>
          )}
          {flash && (
            <Row label={t("sessionDetail.flashmark")}>
              <span className={flash.verdict === "LIVE" ? "text-emerald-500"
                : flash.verdict === "SPOOF" ? "text-red-500" : "text-muted-foreground"}>
                {t(`reason.${flash.verdict}`, t("reason.inconclusive"))}
                {flash.score != null && <> · {pct(flash.score)}</>}
              </span>
            </Row>
          )}
        </div>

        {/* IA vivacidade */}
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-2">{t("sessionDetail.aiLiveness")}</h3>
        <div className="divide-y divide-border/60">
          <Row label={t("sessionDetail.aiProbability")}>
            <strong>{pct(liv.probability)}</strong>
            {liv.ai_model && <span className="text-muted-foreground"> · {liv.ai_model}</span>}
          </Row>
          <Row label={t("sessionDetail.aiVerdict")}>{liv.reason ? t(`reason.${liv.reason}`, liv.reason) : "—"}</Row>
          <Row label={`${t("sessionDetail.padAnalysis")}${deep.liveness_score != null ? ` · ${pct(deep.liveness_score)}` : ""}`}>
            <EvidenceChips items={report.liveness_evidence} />
          </Row>
        </div>

        {/* IA gesto */}
        {ges.gesture && (
          <>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-2">{t("sessionDetail.aiGesture")}</h3>
            <div className="divide-y divide-border/60">
              <Row label={t("sessionDetail.gestureChallenge")}>{ges.gesture ? t(`gesture.instr.${ges.gesture}`, ges.instruction || ges.gesture) : (ges.instruction || "—")}</Row>
              <Row label={t("sessionDetail.aiProbabilityGesture")}>
                <strong>{pct(ges.probability)}</strong>
                {ges.same_person != null && (
                  <span className="text-muted-foreground"> · {t("sessionDetail.samePerson")} {pct(ges.same_person)}</span>
                )}
              </Row>
              <Row label={t("sessionDetail.aiVerdictGesture")}>{ges.reason ? t(`reason.${ges.reason}`, ges.reason) : "—"}</Row>
              <Row label={`${t("sessionDetail.padAnalysisGesture")}${deep.gesture_score != null ? ` · ${pct(deep.gesture_score)}` : ""}`}>
                <EvidenceChips items={report.gesture_evidence} />
              </Row>
            </div>
          </>
        )}

        {/* Metadata footer */}
        <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
          <span>{t("sessionDetail.session")} <code className="font-mono">{d.id}</code></span>
          {d.company_name && <span>{t("sessionDetail.client")}: {d.company_name}</span>}
          <span>{t("sessionDetail.created")}: {fmtUtc(d.created)}</span>
          {d.attempts != null && <span>{t("sessionDetail.attempts")}: {d.attempts}</span>}
          {d.language && <span>{t("sessionDetail.language")}: {d.language.toUpperCase()}</span>}
        </div>
      </CardContent></Card>
    </div>
  );
}
